/**
 * Student Portal — Request a Component wizard.
 *
 * Uses the existing POST /api/orders endpoint UNCHANGED. Every backend
 * requirement identified in the audit is preserved:
 *   • items[] with component_id + quantity
 *   • requested_admin_id  (backend 400 if missing or inactive admin)
 *   • patient_info: name / pid / phone / stage1_surgery_date / prosthesis_type
 *   • photo_url (optional) via POST /api/uploads/photo
 *   • notes (optional)
 *
 * Business rules preserved: defaulter 403, faculty validation, stock
 * reservation (reissuable-first fallback to fresh), waitlist fallback for
 * out-of-stock items, full Order response object.
 *
 * UI additions only:
 *   1. Out-of-stock disable + explicit "Join waitlist" affordance
 *   2. − 1 + quantity stepper in step 1
 *   3. Cleaner Step 2 layout (faculty first, then patient grid)
 *   4. Step 3 review as a real visual card
 *   5. Step 4 SUCCESS screen with the backend's real request ID + CTAs
 *   6. Proper 403 defaulter message surfacing
 *   7. Duplicate-submission lock
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Trash2, Check, ChevronRight, Upload, Search, Minus, Plus,
  CheckCircle2, User as UserIcon, GraduationCap, AlertTriangle,
} from 'lucide-react';
import {
  Modal, SearchInput, StatusPill, ComponentSilhouette,
} from '../ui';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const emptyPatient = () => ({
  name: '',
  pid: '',
  phone: '',
  stage1_surgery_date: '',
  prosthesis_type: 'Screw Retained',
});

const availabilityOf = (c) => {
  const stock = Number(c?.stock_quantity || 0);
  const reissue = Number(c?.reissuable_stock || 0);
  const total = stock + reissue;
  if (total <= 0) return { bucket: 'out',  available: 0,     label: 'Out of stock',  variant: 'bad'  };
  const thr = Number(c?.low_stock_threshold || 0);
  if (total <= thr) return { bucket: 'low', available: total, label: 'Low stock',    variant: 'warn' };
  return                { bucket: 'in',  available: total, label: 'In stock',      variant: 'ok'   };
};

export default function NewRequestPage({ open, onClose, ctx, prefill }) {
  const { components, faculty, showToast, refresh, goto } = ctx;
  const [step, setStep] = useState(1);
  const [items, setItems] = useState([{ component_id: '', quantity: 1 }]);
  const [patient, setPatient] = useState(emptyPatient());
  const [facultyId, setFacultyId] = useState('');
  const [notes, setNotes] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [componentQuery, setComponentQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);  // full backend Order response
  const [submitError, setSubmitError] = useState(''); // long-form error surfaced above submit CTA

  // ── Reset on open ────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setPatient(emptyPatient());
    setFacultyId('');
    setNotes('');
    setPhotoFile(null);
    setPhotoPreview('');
    setComponentQuery('');
    setSubmitting(false);
    setSubmitted(null);
    setSubmitError('');
    if (prefill?.component_id) {
      const qty = Math.max(1, Number(prefill.quantity) || 1);
      setItems([{ component_id: prefill.component_id, quantity: qty }]);
      setStep(2);   // fast-path from Library
    } else {
      setItems([{ component_id: '', quantity: 1 }]);
      setStep(1);
    }
  }, [open, prefill]);

  // ── Helpers ──────────────────────────────────────────────
  const findComponent = (id) => components.find(c => c.id === id);

  const filteredComponents = useMemo(() => {
    const needle = componentQuery.trim().toLowerCase();
    if (!needle) return components.slice(0, 40);
    return components.filter(c => {
      const hay = `${c.name || ''} ${c.brand_name || ''} ${c.size || ''}`.toLowerCase();
      return hay.includes(needle);
    }).slice(0, 40);
  }, [components, componentQuery]);

  const validItems = useMemo(
    () => items.filter(i => i.component_id && Number(i.quantity) > 0),
    [items]
  );

  const anyOutOfStockSelected = useMemo(
    () => validItems.some(i => availabilityOf(findComponent(i.component_id)).bucket === 'out'),
    [validItems, components]
  );

  // ── Step 1: component select (multi-item) ───────────────
  // Clicking a component tile adds it to the request. If it's already in
  // the list, its quantity is bumped by 1. Each item keeps its own qty
  // stepper and remove button. The backend already accepts `items: List`,
  // so multiple components ship in a single request/approval round.
  const addItem = (component_id) => {
    const c = findComponent(component_id);
    if (!c) return;
    const cap = Math.max(1, availabilityOf(c).available || Number.MAX_SAFE_INTEGER);
    const existing = items.find(i => i.component_id === component_id);
    if (existing) {
      const next = Math.min(cap, Number(existing.quantity) + 1);
      setItems(items.map(i => i.component_id === component_id ? { ...i, quantity: next } : i));
      return;
    }
    // Replace the initial empty slot the wizard starts with; otherwise append
    if (items.length === 1 && items[0].component_id === '') {
      setItems([{ component_id, quantity: 1 }]);
    } else {
      setItems([...items, { component_id, quantity: 1 }]);
    }
  };
  const removeItemAt = (index) => {
    const next = items.filter((_, i) => i !== index);
    setItems(next.length ? next : [{ component_id: '', quantity: 1 }]);
  };
  const setQtyAt = (index, quantity) => {
    const it = items[index];
    if (!it?.component_id) return;
    const c = findComponent(it.component_id);
    const cap = Math.max(1, availabilityOf(c).available || Number.MAX_SAFE_INTEGER);
    const n = Math.max(1, Math.min(cap, Number(quantity) || 1));
    setItems(items.map((row, i) => i === index ? { ...row, quantity: n } : row));
  };
  const bumpQtyAt = (index, delta) => {
    const it = items[index];
    if (!it?.component_id) return;
    setQtyAt(index, Number(it.quantity) + delta);
  };

  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('File must be under 5 MB.', 'bad');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const canProceedStep1 = validItems.length > 0;
  const canProceedStep2 = patient.name.trim() && patient.pid.trim()
    && patient.phone.trim() && patient.stage1_surgery_date && facultyId;

  // ── Submission ───────────────────────────────────────────
  const submit = async () => {
    if (!canProceedStep1 || !canProceedStep2 || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      let photo_url = '';
      if (photoFile) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        const upl = await apiClient.post(endpoints.uploads.photo, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        photo_url = upl.data?.photo_url || '';
      }

      const { data: created } = await apiClient.post(endpoints.orders.create, {
        patient_info: { ...patient, photo_url },
        items: validItems,
        notes,
        requested_admin_id: facultyId,
      });

      setSubmitted(created);
      setStep(4);
      // Refresh in background so My Requests / stock counters are fresh
      Promise.all([refresh.orders(), refresh.waitlist(), refresh.components()]).catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Could not submit the request. Please try again.';
      if (Array.isArray(detail)) msg = detail.map(d => d.msg).join('; ');
      else if (typeof detail === 'string') msg = detail;
      else if (err.message) msg = err.message;
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const goToRequests = () => {
    onClose();
    goto && goto('requests');
  };
  const goToLibrary = () => {
    onClose();
    goto && goto('library');
  };

  // ── Wizard chrome ────────────────────────────────────────
  const stepLabel = (n, label) => (
    <div className={`sp-wizard-step${step > n ? ' is-done' : step === n ? ' is-current' : ''}`}>
      <span className="sp-wizard-step-dot">{step > n ? <Check size={12} strokeWidth={2.4} /> : n}</span>
      <span>{label}</span>
    </div>
  );

  const footer = step === 1 ? (
    <>
      <button type="button" className="sp-btn sp-btn-ghost" onClick={onClose} data-testid="nr-cancel">Cancel</button>
      <button
        type="button"
        className="sp-btn sp-btn-primary"
        disabled={!canProceedStep1 || anyOutOfStockSelected}
        onClick={() => setStep(2)}
        data-testid="nr-next-1"
      >Continue <ChevronRight size={14} /></button>
    </>
  ) : step === 2 ? (
    <>
      <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setStep(1)} data-testid="nr-back-2">Back</button>
      <button
        type="button"
        className="sp-btn sp-btn-primary"
        disabled={!canProceedStep2}
        onClick={() => { setSubmitError(''); setStep(3); }}
        data-testid="nr-next-2"
      >Review <ChevronRight size={14} /></button>
    </>
  ) : step === 3 ? (
    <>
      <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setStep(2)} disabled={submitting} data-testid="nr-back-3">Back</button>
      <button
        type="button"
        className="sp-btn sp-btn-accent"
        disabled={submitting}
        onClick={submit}
        data-testid="nr-submit"
      >{submitting ? 'Submitting…' : 'Submit Request'}</button>
    </>
  ) : null; // step 4 success footer is inline

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      wide
      title={step === 4 ? 'Request Submitted' : 'Request a Component'}
      subtitle={step === 4 ? 'Your request has been sent for approval.' : 'Find, choose, review and submit — fast.'}
      footer={footer}
      testId="new-request-modal"
    >
      {step !== 4 && (
        <div className="sp-wizard-steps">
          {stepLabel(1, 'Component')}
          <div className="sp-wizard-divider" />
          {stepLabel(2, 'Details')}
          <div className="sp-wizard-divider" />
          {stepLabel(3, 'Review')}
        </div>
      )}

      {/* ── STEP 1 · SELECT COMPONENT ────────────────────── */}
      {step === 1 && (
        <div>
          <SearchInput
            value={componentQuery}
            onChange={setComponentQuery}
            placeholder="Search implants, abutments, healing abutments…"
            testId="nr-search"
            autoFocus
          />
          <div
            className="sp-mt-4"
            style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--diis-hairline)', borderRadius: 'var(--diis-radius)' }}
            data-testid="nr-component-list"
          >
            {components.length === 0 ? (
              <div className="sp-subtle" style={{ padding: 18, textAlign: 'center' }}>
                No components have been catalogued yet — ask your administrator to upload inventory.
              </div>
            ) : filteredComponents.length === 0 ? (
              <div className="sp-subtle" style={{ padding: 18, textAlign: 'center' }}>
                No matches for “{componentQuery}”.
              </div>
            ) : filteredComponents.map(c => {
              const isSelected = items.some(i => i.component_id === c.id);
              const avail = availabilityOf(c);
              const isOut = avail.bucket === 'out';
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => !isOut && addItem(c.id)}
                  disabled={isOut}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '12px 14px',
                    border: 0,
                    borderBottom: '1px solid var(--diis-hairline)',
                    background: isSelected ? 'var(--diis-accent-soft)' : 'transparent',
                    cursor: isOut ? 'not-allowed' : 'pointer',
                    opacity: isOut ? 0.55 : 1,
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  data-testid={`nr-comp-${c.id}`}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <ComponentSilhouette brand={c.brand_name} name={c.name} size={36} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--diis-ink)' }}>{c.name}</div>
                      <div className="sp-subtle" style={{ marginTop: 2 }}>
                        {c.brand_name || 'Unknown brand'}{c.size ? ` · ${c.size}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                    <span className="sp-mono-num sp-subtle">Avail {avail.available}</span>
                    {isOut && <StatusPill variant="bad">Out of stock</StatusPill>}
                    {!isOut && avail.bucket === 'low' && <StatusPill variant="warn">Low</StatusPill>}
                    {isSelected && !isOut && <StatusPill variant="info">Selected</StatusPill>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected panel — one row per selected component */}
          {validItems.length > 0 && (
            <div
              className="sp-mt-5"
              style={{ padding: 16, border: '1px solid var(--diis-hairline-strong)', borderRadius: 'var(--diis-radius-lg)', background: 'var(--diis-surface)' }}
              data-testid="nr-selected-panel"
            >
              <div className="sp-flex-between">
                <span className="sp-eyebrow">
                  Selected · <span className="sp-mono-num" style={{ color: 'var(--diis-ink)' }}>{validItems.length}</span> component{validItems.length === 1 ? '' : 's'}
                </span>
                <span className="sp-subtle">Tip: click another component to add it</span>
              </div>
              <div className="sp-mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map((it, idx) => {
                  if (!it.component_id) return null;
                  const c = findComponent(it.component_id);
                  if (!c) return null;
                  const avail = availabilityOf(c);
                  const wantsWaitlist = Number(it.quantity) > avail.available;
                  return (
                    <div
                      key={it.component_id}
                      className="sp-flex-between"
                      style={{
                        gap: 12, alignItems: 'center',
                        padding: 12,
                        border: '1px solid var(--diis-hairline)',
                        borderRadius: 'var(--diis-radius)',
                        background: 'var(--diis-surface-sunk)',
                      }}
                      data-testid={`nr-selected-row-${c.id}`}
                    >
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, flex: 1 }}>
                        <ComponentSilhouette brand={c.brand_name} name={c.name} size={40} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 15, color: 'var(--diis-ink)' }}>{c.name}</div>
                          <div className="sp-subtle" style={{ marginTop: 2 }}>
                            {c.brand_name || '—'}{c.size ? ` · ${c.size}` : ''} · <span className="sp-mono-num">{avail.available}</span> available
                          </div>
                          {wantsWaitlist && (
                            <div className="sp-mt-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <StatusPill variant="warn">Will be waitlisted</StatusPill>
                              <span className="sp-subtle" style={{ fontSize: 11 }}>Requesting more than available</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                        <div className="sp-qty" data-testid={`nr-qty-stepper-${c.id}`}>
                          <button type="button" onClick={() => bumpQtyAt(idx, -1)} disabled={it.quantity <= 1} aria-label="Decrease"><Minus size={14} /></button>
                          <input
                            type="number"
                            min={1}
                            value={it.quantity}
                            onChange={(e) => setQtyAt(idx, e.target.value)}
                            data-testid={`nr-qty-${c.id}`}
                          />
                          <button type="button" onClick={() => bumpQtyAt(idx, 1)} aria-label="Increase"><Plus size={14} /></button>
                        </div>
                        <button
                          type="button"
                          className="sp-btn sp-btn-ghost sp-btn-sm"
                          onClick={() => removeItemAt(idx)}
                          aria-label="Remove component"
                          data-testid={`nr-remove-${c.id}`}
                        ><Trash2 size={13} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2 · DETAILS ─────────────────────────────── */}
      {step === 2 && (
        <div>
          <div className="sp-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <GraduationCap size={12} /> Faculty
          </div>
          <p className="sp-subtle sp-mt-2" style={{ marginBottom: 10 }}>
            Your request goes to this faculty for approval. Once approved, the clerk issues the components.
          </p>
          <select
            className="sp-select"
            value={facultyId}
            onChange={(e) => setFacultyId(e.target.value)}
            data-testid="nr-faculty-select"
          >
            <option value="">— Select faculty —</option>
            {faculty.map(f => <option key={f.id} value={f.id}>{f.full_name} ({f.username})</option>)}
          </select>

          <hr className="sp-mt-5" style={{ border: 0, borderTop: '1px solid var(--diis-hairline)' }} />

          <div className="sp-mt-4 sp-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <UserIcon size={12} /> Patient / Case
          </div>
          <div className="sp-grid-2 sp-mt-3">
            <div>
              <label className="sp-field-label">Patient Name</label>
              <input className="sp-input" value={patient.name}
                onChange={(e) => setPatient({ ...patient, name: e.target.value })}
                data-testid="nr-patient-name" required />
            </div>
            <div>
              <label className="sp-field-label">Patient ID (PID)</label>
              <input className="sp-input" value={patient.pid}
                onChange={(e) => setPatient({ ...patient, pid: e.target.value })}
                data-testid="nr-patient-pid" required />
            </div>
            <div>
              <label className="sp-field-label">Phone</label>
              <input className="sp-input" type="tel" value={patient.phone}
                onChange={(e) => setPatient({ ...patient, phone: e.target.value })}
                data-testid="nr-patient-phone" required />
            </div>
            <div>
              <label className="sp-field-label">Stage-1 Surgery Date</label>
              <input className="sp-input" type="date" value={patient.stage1_surgery_date}
                onChange={(e) => setPatient({ ...patient, stage1_surgery_date: e.target.value })}
                data-testid="nr-patient-surgery" required />
            </div>
            <div>
              <label className="sp-field-label">Prosthesis Type</label>
              <select className="sp-select" value={patient.prosthesis_type}
                onChange={(e) => setPatient({ ...patient, prosthesis_type: e.target.value })}
                data-testid="nr-patient-prosthesis">
                <option value="Screw Retained">Screw Retained</option>
                <option value="Cement Retained">Cement Retained</option>
              </select>
            </div>
            <div>
              <label className="sp-field-label">Photo (optional)</label>
              <label className="sp-btn sp-btn-ghost" style={{ display: 'inline-flex', gap: 8 }}>
                <Upload size={14} /> {photoFile ? photoFile.name.slice(0, 22) : 'Upload photo'}
                <input type="file" accept="image/*" onChange={onPhotoChange} style={{ display: 'none' }} data-testid="nr-patient-photo" />
              </label>
              {photoPreview && (
                <img src={photoPreview} alt="preview" style={{ marginTop: 8, height: 48, width: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--diis-hairline)' }} />
              )}
            </div>
          </div>

          <div className="sp-mt-4">
            <label className="sp-field-label">Notes (optional)</label>
            <textarea className="sp-textarea" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the faculty should know about this request."
              data-testid="nr-notes" />
          </div>
        </div>
      )}

      {/* ── STEP 3 · REVIEW ──────────────────────────────── */}
      {step === 3 && (
        <div>
          <div className="sp-eyebrow">Request Summary</div>
          <p className="sp-subtle sp-mt-2" style={{ marginBottom: 14 }}>
            Please confirm — you cannot edit a request once submitted (only cancel).
          </p>

          {submitError && (
            <div
              className="sp-mt-3"
              role="alert"
              data-testid="nr-submit-error"
              style={{
                padding: 12,
                borderRadius: 'var(--diis-radius)',
                background: 'rgba(176, 74, 60, 0.10)',
                border: '1px solid rgba(176, 74, 60, 0.25)',
                color: '#8A3A2E',
                fontSize: 13,
                display: 'flex', gap: 10, alignItems: 'start',
              }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div><strong style={{ fontWeight: 600 }}>Could not submit.</strong><br />{submitError}</div>
            </div>
          )}

          {/* Component(s) */}
          <div className="sp-card sp-mt-4" style={{ padding: 16 }} data-testid="nr-review-component">
            <div className="sp-flex-between">
              <span className="sp-eyebrow">Components</span>
              <span className="sp-subtle sp-mono-num">
                {validItems.length} item{validItems.length === 1 ? '' : 's'} · Total qty {validItems.reduce((s, it) => s + Number(it.quantity || 0), 0)}
              </span>
            </div>
            <div className="sp-mt-3" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {validItems.map((it) => {
                const c = findComponent(it.component_id);
                if (!c) return null;
                const avail = availabilityOf(c);
                const waitlist = Number(it.quantity) > avail.available;
                return (
                  <div
                    key={it.component_id}
                    className="sp-flex-between"
                    style={{ gap: 12, paddingBottom: 8, borderBottom: '1px solid var(--diis-hairline)' }}
                    data-testid={`nr-review-item-${c.id}`}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                      <ComponentSilhouette brand={c.brand_name} name={c.name} size={40} />
                      <div>
                        <div style={{ fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 15 }}>{c.name}</div>
                        <div className="sp-subtle" style={{ marginTop: 2 }}>
                          {c.brand_name}{c.size ? ` · ${c.size}` : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="sp-mono-num" style={{ fontSize: 15, fontWeight: 500 }}>Qty {it.quantity}</div>
                      {waitlist && <StatusPill variant="warn">Waitlist</StatusPill>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Patient */}
          <div className="sp-card sp-mt-4" style={{ padding: 16 }} data-testid="nr-review-patient">
            <div className="sp-eyebrow">Patient / Case</div>
            <div className="sp-grid-2 sp-mt-3">
              <div><span className="sp-subtle">Name</span><div>{patient.name}</div></div>
              <div><span className="sp-subtle">PID</span><div>{patient.pid}</div></div>
              <div><span className="sp-subtle">Phone</span><div>{patient.phone}</div></div>
              <div><span className="sp-subtle">Stage 1</span><div>{patient.stage1_surgery_date}</div></div>
              <div><span className="sp-subtle">Prosthesis</span><div>{patient.prosthesis_type}</div></div>
              <div><span className="sp-subtle">Faculty</span><div>{faculty.find(f => f.id === facultyId)?.full_name || '—'}</div></div>
            </div>
          </div>

          {notes && (
            <div className="sp-card sp-mt-4" style={{ padding: 16 }} data-testid="nr-review-notes">
              <div className="sp-eyebrow">Notes</div>
              <div className="sp-mt-2" style={{ fontSize: 13, color: 'var(--diis-ink-muted)' }}>{notes}</div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 4 · SUCCESS ─────────────────────────────── */}
      {step === 4 && submitted && (
        <div style={{ textAlign: 'center', padding: '8px 12px 4px' }} data-testid="nr-success">
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--diis-success-soft)', color: 'var(--diis-success)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <CheckCircle2 size={36} strokeWidth={1.6} />
          </div>
          <h3
            style={{
              fontFamily: 'var(--diis-font-display)', fontWeight: 500,
              fontSize: 24, letterSpacing: '-0.014em', color: 'var(--diis-ink)',
              margin: 0,
            }}
            data-testid="nr-success-title"
          >Request submitted.</h3>
          <p className="sp-subtle sp-mt-2" style={{ maxWidth: 460, margin: '8px auto 0' }}>
            Your request has been sent to <strong>{submitted.requested_admin_name || 'the assigned faculty'}</strong> for approval.
          </p>

          <div
            className="sp-mt-5"
            style={{
              display: 'inline-flex', flexDirection: 'column', gap: 6,
              padding: '14px 22px',
              border: '1px solid var(--diis-hairline)',
              borderRadius: 'var(--diis-radius-lg)',
              background: 'var(--diis-surface)',
              minWidth: 260,
            }}
          >
            <span className="sp-eyebrow">Request ID</span>
            <span
              className="sp-mono-num"
              style={{ fontFamily: 'var(--diis-font-mono)', fontSize: 15, color: 'var(--diis-ink)', letterSpacing: '0.04em' }}
              data-testid="nr-success-request-id"
            >REQ-{submitted.id.slice(-8).toUpperCase()}</span>
            <div style={{ marginTop: 6 }}>
              <StatusPill variant="warn">Status · Pending approval</StatusPill>
            </div>
            {submitted.has_returnable_items && (
              <div className="sp-subtle sp-mt-2">
                Contains returnable items. A return timeline will be set on approval.
              </div>
            )}
          </div>

          <div className="sp-mt-6" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="sp-btn sp-btn-primary"
              onClick={goToRequests}
              data-testid="nr-success-view-request"
            >View Request</button>
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              onClick={goToLibrary}
              data-testid="nr-success-return-library"
            >Return to Components</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
